import React from "react";
import * as Icons from "@mui/icons-material";
import {
  Drawer,
  List,
  ListItem,
  ListItemText,
  CircularProgress,
  Collapse,
  Box,
  Typography,
  IconButton,
} from "@mui/material";
import {
  StyledMenuItemButton,
  StyledHeaderBox,
  StyledUserInfoBox,
  StyledMenuContainer,
  StyledFooterBox,
  StyledVersionText,
} from "./DrawerMenu.styles";
import { Logout } from "@mui/icons-material";
import { Theme } from "@mui/material/styles";

export interface MenuItem {
  id: string;
  name: string;
  route: string;
  path?: string;
  icon?: string;
  order: number;
  children?: MenuItem[];
}

export interface DrawerMenuPresenterProps {
  isOpen: boolean;
  onClose: () => void;
  menuItems: MenuItem[];
  isLoading: boolean;
  error: unknown;
  userInfo?: {
    name?: string;
    email?: string;
    role?: string;
  };
  openSubMenus: Record<string, boolean>;
  onToggleSubMenu: (menuId: string) => void;
  onNavigate: (path: string) => void;
  currentPath: string;
  isMobile?: boolean;
  appVersion?: {
    functionalVersion?: string;
    technicalVersion?: string;
  };
  onVersionClick?: () => void;
  onLogout?: () => void;
  theme: Theme;
}

export const DrawerMenuPresenter: React.FC<DrawerMenuPresenterProps> = ({
  isOpen,
  onClose,
  menuItems,
  isLoading,
  error,
  userInfo,
  openSubMenus,
  onToggleSubMenu,
  onNavigate,
  currentPath,
  isMobile = false,
  appVersion,
  onVersionClick,
  onLogout,
}) => {
  // Dynamic icon loader
  const getIcon = (iconName?: string) => {
    if (!iconName) return null;
    const IconComponent = Icons[iconName as keyof typeof Icons];
    return IconComponent ? <IconComponent /> : null;
  };

  // Loading state
  if (isLoading) {
    return (
      <Drawer anchor="left" open={isOpen} onClose={onClose}>
        <Box
          sx={{
            width: 240,
            height: "100vh",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: "menu.main",
          }}
        >
          <CircularProgress sx={{ color: "menu.light2" }} />
        </Box>
      </Drawer>
    );
  }

  // Error state
  if (error) {
    return (
      <Drawer anchor="left" open={isOpen} onClose={onClose}>
        <Box sx={{ width: 240, p: 3, bgcolor: "menu.main" }}>
          <Typography variant="bodyMedium" sx={{ color: "menu.light2" }}>
            Error loading menu. Please try again.
          </Typography>
        </Box>
      </Drawer>
    );
  }

  const renderMenuItem = (item: MenuItem, depth: number = 0) => {
    const hasChildren = item.children && item.children.length > 0;
    const isActive = currentPath === item.path;
    const isOpenMenu = openSubMenus[item.id];

    const handleClick = () => {
      if (hasChildren) {
        onToggleSubMenu(item.id);
      } else {
        onNavigate(item.route);
      }
    };

    const menuContent = (
      <>
        {item.icon && getIcon(item.icon)}
        <ListItemText primary={item.name} sx={{ m: 0 }} />
        {hasChildren && (
          <Box sx={{ display: "flex", alignItems: "center", ml: "auto" }}>
            {isOpenMenu ? getIcon("ExpandLess") : getIcon("ExpandMore")}
          </Box>
        )}
      </>
    );

    return (
      <React.Fragment key={item.id}>
        <ListItem disablePadding>
          <StyledMenuItemButton
            active={isActive}
            depth={depth}
            onClick={handleClick}
          >
            {menuContent}
          </StyledMenuItemButton>
        </ListItem>

        {hasChildren && (
          <Collapse in={isOpenMenu} timeout="auto" unmountOnExit>
            <List component="div" disablePadding sx={{ ml: 2 }}>
              {item
                .children!.sort((a, b) => a.order - b.order)
                .map((child) => renderMenuItem(child, depth + 1))}
            </List>
          </Collapse>
        )}
      </React.Fragment>
    );
  };

  return (
    <Drawer
      anchor="left"
      variant={isMobile ? "temporary" : "persistent"}
      open={isOpen}
      onClose={onClose}
      ModalProps={{
        keepMounted: true,
      }}
      sx={{
        width: 240,
        flexShrink: 0,
        "& .MuiDrawer-paper": {
          width: 240,
          boxSizing: "border-box",
          backgroundColor: "menu.main",
          borderRight: "none",
        },
      }}
    >
      <Box
        sx={{
          width: 240,
          height: "100%",
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          p: 2,
          bgcolor: "menu.main",
        }}
      >
        {/* Top Section */}
        <Box sx={{ display: "flex", flexDirection: "column" }}>
          {/* Header */}
          <StyledHeaderBox>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                mt: -2,
                height: "5.125rem",
                p: 0,
              }}
            >
              <Box
                component="img"
                src="/assets/logo@2x.png"
                alt="COYN"
                sx={{ height: 48, width: "auto" }}
                onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
                  e.currentTarget.style.display = "none";
                }}
              />
              <Typography
                variant="headlineLarge"
                sx={{
                  color: "textMain.light",
                  fontSize: "1.5rem",
                  lineHeight: "2rem",
                }}
              >
                COYN
              </Typography>
              {isMobile && (
                <IconButton
                  onClick={onClose}
                  size="small"
                  sx={{ color: "textMain.light" }}
                >
                  {getIcon("Close")}
                </IconButton>
              )}
            </Box>
          </StyledHeaderBox>

          {/* User Info */}
          {userInfo && (
            <StyledUserInfoBox>
              <Typography
                variant="titleSmall"
                sx={{
                  color: "textMain.main3",
                  textAlign: "center",
                  width: "100%",
                }}
              >
                {userInfo.name}
              </Typography>
              <Typography
                variant="labelSmall"
                sx={{
                  color: "textMain.main3",
                  textAlign: "center",
                  width: "100%",
                }}
              >
                {userInfo.role}
              </Typography>
              <Typography
                variant="labelSmall"
                sx={{
                  color: "textMain.main3",
                  textAlign: "center",
                  width: "100%",
                }}
              >
                {userInfo.email}
              </Typography>
            </StyledUserInfoBox>
          )}

          {/* Menu */}
          <StyledMenuContainer>
            <List sx={{ p: 0 }}>
              {menuItems
                .sort((a, b) => a.order - b.order)
                .map((item) => renderMenuItem(item))}
            </List>
          </StyledMenuContainer>
        </Box>

        {/* Bottom Section */}
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          {/* Footer */}
          <StyledFooterBox>
            <IconButton onClick={() => onLogout && onLogout()}>
              <Logout sx={{ mr: 1, color: "menu.dark2" }} />
              <Typography
                variant="titleMedium"
                sx={{ color: "menu.dark2", mb: 0.5 }}
              >
                Sign out
              </Typography>
            </IconButton>
            <Typography
              variant="labelSmall"
              sx={{ color: "menu.dark2", mb: 0.5 }}
            >
              CMI © {new Date().getFullYear()}
            </Typography>
            {appVersion?.functionalVersion && (
              <StyledVersionText
                variant="labelSmall"
                clickable={!!onVersionClick}
                onClick={onVersionClick}
                sx={{ color: "menu.dark2" }}
              >
                {appVersion.functionalVersion}
              </StyledVersionText>
            )}
          </StyledFooterBox>
        </Box>
      </Box>
    </Drawer>
  );
};
